import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, Operation } from '@apollo/client';
import { DefinitionNode, OperationDefinitionNode } from 'graphql';
import { isEffectivelyOnline } from '@bilderit/offline-link';
import {
  isAnalyticsModule,
  resolveModuleForPath,
} from '@/utils/analytics/taxonomy';
import { capture } from '@/utils/analytics/capture';
import {
  getPendingReportEntryPoint,
  isReportEntryPoint,
  type ReportEntryPoint,
} from '@/utils/analytics/reportEntryPoint';

/**
 * Emits one `mutation_completed` product-analytics event per GraphQL
 * mutation (queries are ignored — see docs/product-analytics-contract.md
 * §"In-house vs PostHog: what goes where", which forbids a
 * per-GraphQL-operation event stream at query volume).
 *
 * This file exports TWO links that only make sense together, on opposite
 * sides of `createOfflineLink` in links.ts:
 *
 *   analyticsIntentLink   (upstream)   — stamps what the user meant, at
 *                                        submit time
 *   createOfflineLink     — persists tracked mutations (IndexedDB) and
 *                           holds everything while offline
 *   analyticsLink         (downstream) — captures the outcome, once per
 *                                        genuine network attempt
 *
 * Why two: `analyticsLink` must sit downstream of `createOfflineLink` so it
 * sees each network attempt exactly once (upstream it would count a
 * mutation on submit AND again on replay). But downstream it only runs when
 * the request actually goes out — for a mutation submitted offline that is
 * minutes or days later, possibly after a reload, from another screen —
 * so anything computed at send time — `was_offline` by this link, `module`
 * and `route_name` by `capture()` from the current screen — would describe
 * the replay, not the action. `analyticsIntentLink` therefore records those
 * at submit time into the operation context, under
 * `ANALYTICS_INTENT_CONTEXT_KEY`, and `analyticsLink` prefers that record
 * over both.
 *
 * How the record survives the gap:
 * - No reload: the offline package's queue holds the very same Operation
 *   object and forwards it later, context included.
 * - Reload: for tracked mutations (`useOfflineMutation`, which is also the
 *   hook used while online — the stamp is taken on every submit, tracked or
 *   not, and is right either way) `asyncTrackerLink` persists
 *   `operation.getContext()` to IndexedDB — the stamp is already in it
 *   because this link runs first — and `OfflineMutationLoader` replays with
 *   that persisted `context`. The replay goes through the whole chain again,
 *   so `analyticsIntentLink` is idempotent: it never overwrites an existing
 *   stamp. That idempotence is the load-bearing rule; without it a replay
 *   would re-stamp itself as an online action.
 * - The stamp is plain JSON (numbers, a boolean, strings) because the
 *   tracked-query store serialises the context.
 *
 * Neither link changes anything under packages/offline-link.
 */

// Mirrors trimMutationStringVariablesLink.ts's private isMutationOperation
// check (replicated rather than imported — that helper isn't exported).
const isMutationOperation = (
  definitions: readonly DefinitionNode[],
  operationName: string
): boolean => {
  const operations = definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition'
  );
  const operation = operationName
    ? operations.find((definition) => definition.name?.value === operationName)
    : operations[0];
  return operation?.operation === 'mutation';
};

const NETWORK_QUALITY_THRESHOLD = '3g';

/**
 * Operation-context key under which `analyticsIntentLink` stores the
 * submit-time facts that `analyticsLink` reports. Shared by the two links
 * (and, transitively, persisted by the offline package's tracked-query
 * store — the key is part of what lands in IndexedDB).
 */
export const ANALYTICS_INTENT_CONTEXT_KEY = 'analyticsIntent';

/**
 * Report-create mutations: the only operations that carry `entry_point` on
 * `mutation_completed` (BIL-5324). Extend when another create mutation gets
 * a `…/new` screen wired to `useReportStarted`.
 */
export const REPORT_ENTRY_POINT_OPERATIONS: readonly string[] = [
  'AddDailyReport',
  'AddDailyReportOffline',
  'AddMachineNotice',
  'AddFuelDispatchReport',
  'AddStockControlReport',
];

export interface AnalyticsIntent {
  /** `Date.now()` when the user submitted the mutation. */
  submittedAt: number;
  /** Connectivity at submit time (`isEffectivelyOnline('3g')` negated). */
  wasOffline: boolean;
  /** Taxonomy module of the screen the user was on when they submitted. */
  module: string;
  /**
   * Human screen name (`route_name`) at submit time, next to `module`
   * (BIL-5331). Absent when the path matched no route, or in a stamp
   * persisted before this field existed.
   */
  routeName?: string;
  /**
   * How the report-create screen was reached (BIL-5324). Only on
   * REPORT_ENTRY_POINT_OPERATIONS, stamped at submit time so an offline
   * replay still reports the original entry point.
   */
  entryPoint?: ReportEntryPoint;
}

/**
 * Reads a stamp back from the operation context, tolerating anything a
 * persisted (JSON round-tripped, possibly pre-stamp) context might hold.
 */
export const readAnalyticsIntent = (
  operation: Operation
): AnalyticsIntent | undefined => {
  const candidate: unknown =
    operation.getContext()[ANALYTICS_INTENT_CONTEXT_KEY];
  if (typeof candidate !== 'object' || candidate === null) return undefined;
  const { submittedAt, wasOffline, module } =
    candidate as Partial<AnalyticsIntent>;
  if (
    typeof submittedAt !== 'number' ||
    typeof wasOffline !== 'boolean' ||
    typeof module !== 'string'
  ) {
    return undefined;
  }
  const { entryPoint, routeName } = candidate as {
    entryPoint?: unknown;
    routeName?: unknown;
  };
  return {
    submittedAt,
    wasOffline,
    // A stamp persisted before taxonomy v2 (BIL-5331) carries an old value;
    // the list is closed, so it replays as `unknown`, the rest kept.
    module:
      module === 'unknown' || isAnalyticsModule(module) ? module : 'unknown',
    ...(typeof routeName === 'string' ? { routeName } : {}),
    ...(isReportEntryPoint(entryPoint) ? { entryPoint } : {}),
  };
};

/**
 * Upstream half — see the file header. Stamps mutations at submit time and
 * leaves an existing stamp (a replay of a persisted operation) untouched.
 */
export function createAnalyticsIntentLink() {
  return new ApolloLink((operation, forward) => {
    if (
      !isMutationOperation(operation.query.definitions, operation.operationName)
    ) {
      return forward(operation);
    }

    if (readAnalyticsIntent(operation) === undefined) {
      try {
        const { module, routeName } = resolveModuleForPath(
          window.location.pathname
        );
        const intent: AnalyticsIntent = {
          submittedAt: Date.now(),
          wasOffline: !isEffectivelyOnline(NETWORK_QUALITY_THRESHOLD),
          module,
          ...(routeName ? { routeName } : {}),
        };
        if (REPORT_ENTRY_POINT_OPERATIONS.includes(operation.operationName)) {
          const entryPoint = getPendingReportEntryPoint();
          if (entryPoint) intent.entryPoint = entryPoint;
        }
        operation.setContext({ [ANALYTICS_INTENT_CONTEXT_KEY]: intent });
      } catch {
        // Analytics must never affect the data path; without a stamp,
        // analyticsLink falls back to link-time `was_offline` and capture()
        // stamps `module` and `route_name` from the current screen.
      }
    }

    return forward(operation);
  });
}

const captureMutationOutcome = (
  operation: Operation,
  outcome: 'success' | 'error',
  startedAt: number,
  intent: AnalyticsIntent | undefined,
  wasOfflineAtSend: boolean
): void => {
  // Never include the mutation's input variables or any response payload —
  // both routinely carry personal data (contract PII denylist). Only the
  // taxonomy's `module` and `route_name` may describe where this happened.
  capture('mutation_completed', {
    operation_name: operation.operationName,
    outcome,
    // Submit-time values when the intent stamp is present (the normal case,
    // see the file header), `route_name` included even when undefined so
    // capture() never pairs this module with the screen at send time (a
    // replay of a stamp that predates `routeName`).
    ...(intent ? { module: intent.module, route_name: intent.routeName } : {}),
    duration_ms: Date.now() - startedAt,
    was_offline: intent?.wasOffline ?? wasOfflineAtSend,
    ...(intent?.entryPoint ? { entry_point: intent.entryPoint } : {}),
  });
};

/**
 * Downstream half — see the file header. `duration_ms` is still the
 * duration of this network attempt (not submit-to-completion), so a
 * replayed offline mutation reports the replay's own round trip.
 */
export function createAnalyticsLink() {
  return new ApolloLink((operation, forward) => {
    if (
      !isMutationOperation(operation.query.definitions, operation.operationName)
    ) {
      return forward(operation);
    }

    const startedAt = Date.now();
    const intent = readAnalyticsIntent(operation);
    const wasOfflineAtSend = !isEffectivelyOnline(NETWORK_QUALITY_THRESHOLD);

    // Subscribe rather than .map(): map only runs for emitted results, so
    // it would miss the error channel — see backendReleaseLink.ts for the
    // same shape and the same reasoning.
    return new Observable<FetchResult>((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          try {
            const outcome =
              result.errors && result.errors.length > 0 ? 'error' : 'success';
            captureMutationOutcome(
              operation,
              outcome,
              startedAt,
              intent,
              wasOfflineAtSend
            );
          } catch {
            // Analytics must never affect the data path.
          }
          observer.next(result);
        },
        error: (error) => {
          try {
            captureMutationOutcome(
              operation,
              'error',
              startedAt,
              intent,
              wasOfflineAtSend
            );
          } catch {
            // Analytics must never affect the data path.
          }
          observer.error(error);
        },
        complete: () => observer.complete(),
      });
      return () => subscription.unsubscribe();
    });
  });
}

export const analyticsIntentLink = createAnalyticsIntentLink();
export const analyticsLink = createAnalyticsLink();
